import { Box, Checkbox, Flex, Text } from '@chakra-ui/react';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { NormalizedColumn, ResponsiveCardRenderer, ResponsiveCardViewOptions, ResponsiveGridState } from './types';
import { buildResolvedFields, sortFields } from './fieldUtils';
import { useInfiniteCardData } from './createDatasourceAdapter';
import { DefaultCardRenderer } from './DefaultCardRenderer';
import { LoadingCardSkeleton } from './LoadingCardSkeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { IDatasource } from '@ag-grid-community/core';
import { CardSelectionApi, useCardSelection } from './useCardSelection';

type InfiniteCardListViewProps<TData, TColId extends string> = {
    columns: NormalizedColumn<TData, TColId>[];
    datasource: IDatasource | undefined;
    state: ResponsiveGridState;
    stateVersion: number;
    cardView?: ResponsiveCardViewOptions<TData, TColId>;
    cacheBlockSize?: number;
    maxConcurrentDatasourceRequests?: number;
    rowSelection?: 'single' | 'multiple';
    suppressRowClickSelection?: boolean;
    isRowSelectable?: (params: { data: TData | undefined }) => boolean;
    onSelectionChanged?: () => void;
    selectionRef?: MutableRefObject<CardSelectionApi<TData> | null>;

    /** Row click callback (fires in card mode) */
    onRowClicked?: (row: TData, rowIndex: number) => void;

    /** Row selected callback (fires after selection toggle) */
    onRowSelected?: (row: TData, rowIndex: number, selected: boolean) => void;
};

export function InfiniteCardListView<TData, TColId extends string>({
    columns,
    datasource,
    state,
    stateVersion,
    cardView,
    cacheBlockSize,
    maxConcurrentDatasourceRequests,
    rowSelection,
    suppressRowClickSelection,
    isRowSelectable,
    onSelectionChanged,
    selectionRef,
    onRowClicked,
    onRowSelected,
}: InfiniteCardListViewProps<TData, TColId>) {
    const pageSize = cardView?.pageSize ?? cacheBlockSize ?? 50;

    const { rows, isLoading, hasMore, error, loadMore } = useInfiniteCardData<TData>({
        datasource,
        state,
        stateVersion,
        pageSize,
        maxConcurrentRequests: maxConcurrentDatasourceRequests ?? 1,
    });

    const CardRenderer = (cardView?.renderCard ?? DefaultCardRenderer) as ResponsiveCardRenderer<TData, TColId>;
    const LoadingComponent = cardView?.renderLoadingCard ?? LoadingCardSkeleton;
    const EmptyComponent = cardView?.renderEmptyState;

    const layoutSlots = cardView?.layout?.slots;
    const layoutOrder = cardView?.layout?.order;
    const layoutHidden = cardView?.layout?.hidden;

    const selection = useCardSelection<TData>({
        rowSelection,
        isRowSelectable,
        rows,
        onSelectionChanged,
    });

    if (selectionRef) {
        selectionRef.current = selection;
    }

    const prevVersionRef = useRef(stateVersion);
    useEffect(() => {
        if (prevVersionRef.current !== stateVersion) {
            prevVersionRef.current = stateVersion;
            selection.deselectAll();
        }
    }, [stateVersion, selection]);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const overscan = cardView?.overscan ?? 5;

    const loadingSlotCount = isLoading && rows.length > 0 ? Math.min(pageSize, 3) : 0;
    const totalCount = rows.length + loadingSlotCount;

    const virtualizer = useVirtualizer({
        count: totalCount,
        getScrollElement: () => scrollContainerRef.current,
        estimateSize: () => 80,
        gap: 8,
        overscan,
    });

    const virtualItems = virtualizer.getVirtualItems();
    const lastVirtualItem = virtualItems[virtualItems.length - 1];
    const lastVirtualIndex = lastVirtualItem?.index;
    useEffect(() => {
        if (lastVirtualIndex == null) {
            return;
        }
        if (lastVirtualIndex >= rows.length - 1 && hasMore && !isLoading && !error) {
            loadMore();
        }
    }, [lastVirtualIndex, rows.length, hasMore, isLoading, error, loadMore]);

    const handleSelect = useCallback(
        (row: TData, rowIndex: number) => {
            selection.toggleSelection(row, rowIndex);
        },
        [selection],
    );

    const skeletonCount = useMemo(() => Math.min(pageSize, 5), [pageSize]);

    const selectableCount = useMemo(() => {
        if (!isRowSelectable) {
            return rows.length;
        }
        return rows.filter((row) => isRowSelectable({ data: row })).length;
    }, [rows, isRowSelectable]);

    // Initial loading
    if (rows.length === 0 && isLoading) {
        return (
            <Flex flexDirection="column" gap="8px" overflow="auto" h="100%">
                {Array.from({ length: skeletonCount }).map((_, i) => (
                    <LoadingComponent key={i} />
                ))}
            </Flex>
        );
    }

    // Empty state
    if (rows.length === 0 && !isLoading) {
        if (error) {
            return (
                <Flex p="16px" justifyContent="center">
                    <Text color="fg.error">{error}</Text>
                </Flex>
            );
        }

        if (EmptyComponent) {
            return <EmptyComponent />;
        }

        return <EmptyState title="No results found" description="Try adjusting your filters" />;
    }

    const allSelected = selectableCount > 0 && selection.selectedIndices.size === selectableCount;
    const someSelected = selection.selectedIndices.size > 0 && !allSelected;

    return (
        <Flex flexDirection="column" overflow="hidden" h="100%">
            {rowSelection === 'multiple' && rows.length > 0 && (
                <Flex alignItems="center" gap="8px" px="12px" py="6px" flexShrink={0}>
                    <Checkbox.Root
                        checked={someSelected ? 'indeterminate' : allSelected}
                        onCheckedChange={(e) => {
                            if (e.checked) {
                                selection.selectAll();
                            } else {
                                selection.deselectAll();
                            }
                        }}
                        size="sm">
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                    </Checkbox.Root>
                    <Text fontSize="sm" color="fg.muted">
                        {selection.selectedIndices.size > 0
                            ? `${selection.selectedIndices.size} of ${selectableCount} selected`
                            : `Select all (${selectableCount})`}
                    </Text>
                </Flex>
            )}
            <Box ref={scrollContainerRef} overflow="auto" flex="1">
                <Box position="relative" w="100%" style={{ height: `${virtualizer.getTotalSize()}px` }}>
                    {virtualizer.getVirtualItems().map((virtualItem) => {
                        const idx = virtualItem.index;
                        const isLoadingSlot = idx >= rows.length;

                        if (isLoadingSlot) {
                            return (
                                <Box
                                    key={`loading-${idx}`}
                                    ref={virtualizer.measureElement}
                                    data-index={idx}
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    w="100%"
                                    style={{ transform: `translateY(${virtualItem.start}px)` }}>
                                    <LoadingComponent />
                                </Box>
                            );
                        }

                        const row = rows[idx];
                        const allFields = buildResolvedFields(columns, row, idx, layoutSlots, layoutHidden);
                        const orderedFields = sortFields(allFields, layoutOrder);
                        const visibleFields = orderedFields.filter((f) => !f.hidden && f.slot !== 'hidden');

                        const getField = (colId: TColId) => allFields.find((f) => f.colId === colId);

                        return (
                            <Box
                                key={virtualItem.key}
                                ref={virtualizer.measureElement}
                                data-index={idx}
                                position="absolute"
                                top={0}
                                left={0}
                                w="100%"
                                style={{ transform: `translateY(${virtualItem.start}px)` }}>
                                <CardRenderer
                                    row={row}
                                    rowIndex={idx}
                                    isSelected={selection.isSelected(idx)}
                                    isSelectable={selection.isSelectable(row)}
                                    selectionMode={rowSelection}
                                    fields={orderedFields}
                                    visibleFields={visibleFields}
                                    getField={getField}
                                    onSelect={() => {
                                        const willBeSelected = !selection.isSelected(idx);
                                        if (!suppressRowClickSelection) {
                                            handleSelect(row, idx);
                                            onRowSelected?.(row, idx, willBeSelected);
                                        }
                                        onRowClicked?.(row, idx);
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>

                {/* Error during pagination */}
                {error && !isLoading && (
                    <Flex p="12px" justifyContent="center">
                        <Box as="button" onClick={loadMore} color="fg.error" textDecoration="underline" cursor="pointer">
                            <Text fontSize="sm">{error} — Tap to retry</Text>
                        </Box>
                    </Flex>
                )}
            </Box>
        </Flex>
    );
}
